/**
 * Video Source Resolver - S-2219 + S-3011B4
 * Ensures players bind to playable assets (CF Stream or Mux), not thumbnails
 * S-3011B4: Implements playback URL priority (CF > Mux signed > Mux public)
 */

import { edgeInvoke } from '@/lib/edgeInvoke';

export interface VideoRow {
  id: string;
  mux_playback_id?: string | null;
  playback_policy?: string | null;
  status: string;
  // S-3011B4: New fields for unified endpoint
  cf_playback_url?: string | null;
  legacy_video_id?: string | null;
}

export interface PlayableUrlResult {
  url: string | null;
  error?: string;
  fallbackUsed?: boolean;
}

/**
 * Get a playable video URL (HLS stream) for a video record
 * S-3011B4: Priority order - CF Stream URL > Mux signed URL > Mux public URL
 */
export async function getPlayableUrl(video: VideoRow): Promise<PlayableUrlResult> {
  // Validate video is ready for playback
  if (video.status !== 'ready') {
    return {
      url: null,
      error: `Video is ${video.status}, not ready for playback`
    };
  }

  // S-3011B4: Priority 1 - Cloudflare Stream URL (new CF uploads)
  if (video.cf_playback_url) {
    console.log('[S-3011B4] Using CF Stream URL');
    return {
      url: video.cf_playback_url,
      fallbackUsed: false
    };
  }

  // S-3011B4: Priority 2/3 - Mux playback (legacy videos)
  if (!video.mux_playback_id) {
    return {
      url: null,
      error: 'No playback URL available (no CF URL or Mux playback ID)'
    };
  }

  // Strategy A: Try direct Mux public URL for public videos
  if (video.playback_policy === 'public') {
    const publicUrl = `https://stream.mux.com/${video.mux_playback_id}.m3u8`;
    console.log('[S-3011B4] Using Mux public URL');
    return {
      url: publicUrl,
      fallbackUsed: false
    };
  }

  // Strategy B: Get signed URL via edge function for private videos
  try {
    const result = await edgeInvoke('playback-url', { 
      playback_id: video.mux_playback_id 
    });
    
    if (result?.url) {
      console.log('[S-3011B4] Using Mux signed URL');
      return {
        url: result.url,
        fallbackUsed: false
      };
    } else {
      // Edge function didn't return URL, try public fallback
      const fallbackUrl = `https://stream.mux.com/${video.mux_playback_id}.m3u8`;
      console.warn('[S-3011B4] Fallback to Mux public URL - signed URL unavailable');
      return {
        url: fallbackUrl,
        error: 'Using public fallback - signed URL unavailable',
        fallbackUsed: true
      };
    }
  } catch (error) {
    console.error('[S-3011B4] Error getting signed playback URL:', error);
    
    // Final fallback to public URL
    const fallbackUrl = `https://stream.mux.com/${video.mux_playback_id}.m3u8`;
    return {
      url: fallbackUrl,
      error: 'Using public fallback - signed URL failed',
      fallbackUsed: true
    };
  }
}

/**
 * Validate that a URL is a playable video asset (not a thumbnail)
 */
export function isPlayableVideoUrl(url: string): boolean {
  if (!url) return false;
  
  // Check for video streaming formats
  const videoExtensions = ['.m3u8', '.mp4', '.webm', '.mov'];
  const hasVideoExtension = videoExtensions.some(ext => url.includes(ext));
  
  // Check for thumbnail indicators (should NOT be present)
  const thumbnailIndicators = ['thumbnail', 'poster', '.jpg', '.jpeg', '.png', '.webp'];
  const hasThumbnailIndicators = thumbnailIndicators.some(indicator => 
    url.toLowerCase().includes(indicator)
  );
  
  return hasVideoExtension && !hasThumbnailIndicators;
}

/**
 * Get video metadata for display purposes
 * S-3011B4: Updated to check CF playback URL as well
 */
export function getVideoDisplayInfo(video: VideoRow) {
  const hasPlayableSource = !!video.cf_playback_url || !!video.mux_playback_id;
  
  return {
    isReady: video.status === 'ready',
    hasPlaybackId: !!video.mux_playback_id,
    hasCfPlaybackUrl: !!video.cf_playback_url,
    isPublic: video.playback_policy === 'public',
    canPlay: video.status === 'ready' && hasPlayableSource,
    isLegacy: !!video.legacy_video_id
  };
}
